/**
 * Observer read-only de reunião ao vivo (F7 — dashboard manager/admin).
 *
 * Conecta a `wss://<host>/api/v1/meetings/ws/{session_id}/observer` com
 * subprotocolo `copilot.v1, <jwt>`. Apenas recebe eventos — closer comum
 * recebe close 4403 (validado no backend).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Insight } from '@copilot/shared-types';
import { useAuthStore } from '../auth/store';
import { InsightsLiveList } from './InsightsLiveList';

interface LiveSegment {
  key: string;
  segmentGroupId: string;
  speaker: 'closer' | 'client';
  diarizationLabel: string | null;
  text: string;
  isPartial: boolean;
}

interface LiveMetrics {
  talkRatioCloser: number;
  talkRatioCustomer: number;
  wpmCloser: number;
  silenceSecondsTotal: number;
}

const SUBPROTOCOL = 'copilot.v1';

function speakerLabel(seg: LiveSegment): string {
  if (seg.speaker === 'closer') return 'Closer';
  if (seg.diarizationLabel !== null) {
    const idx = seg.diarizationLabel.match(/\d+/);
    if (idx !== null && idx[0] !== '1') return `Cliente ${idx[0]}`;
  }
  return 'Cliente';
}

export function MeetingLiveReadOnly() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const accessToken = useAuthStore((s) => s.accessToken);
  const role = useAuthStore((s) => s.user?.role ?? null);

  const [segments, setSegments] = useState<LiveSegment[]>([]);
  const [metrics, setMetrics] = useState<LiveMetrics | null>(null);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [status, setStatus] = useState<
    'idle' | 'connecting' | 'connected' | 'closed' | 'forbidden'
  >('idle');
  const [closeReason, setCloseReason] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const wsBase = useMemo(
    () =>
      (import.meta.env.VITE_WS_BASE_URL as string | undefined) ??
      (import.meta.env.VITE_API_URL as string).replace(/^http/, 'ws'),
    [],
  );

  useEffect(() => {
    if (sessionId === undefined || accessToken === null) return;
    if (role !== 'manager' && role !== 'admin') {
      setStatus('forbidden');
      return;
    }

    setStatus('connecting');
    const url = `${wsBase}/api/v1/meetings/ws/${sessionId}/observer`;
    const ws = new WebSocket(url, [SUBPROTOCOL, accessToken]);
    wsRef.current = ws;

    ws.addEventListener('open', () => setStatus('connected'));
    ws.addEventListener('error', () => {
      setCloseReason('Erro de conexão');
    });
    ws.addEventListener('close', (ev) => {
      setStatus('closed');
      if (ev.code === 4403) {
        setCloseReason('Acesso negado (apenas manager/admin do tenant).');
        setStatus('forbidden');
      } else if (ev.code === 4401) {
        setCloseReason('Token inválido ou expirado.');
      } else if (ev.reason !== '') {
        setCloseReason(ev.reason);
      }
    });
    ws.addEventListener('message', (ev) => {
      if (typeof ev.data !== 'string') return;
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(ev.data);
      } catch {
        return;
      }
      const t = payload.type as string | undefined;
      if (t === 'transcript.partial' || t === 'transcript.final') {
        const speaker = payload.speaker as 'closer' | 'client';
        const groupId = String(payload.segment_group_id);
        const isPartial = t === 'transcript.partial';
        const seg: LiveSegment = {
          key: isPartial ? groupId : String(payload.segment_id),
          segmentGroupId: groupId,
          speaker,
          diarizationLabel: (payload.diarization_label as string | undefined) ?? null,
          text: String(payload.text),
          isPartial,
        };
        setSegments((prev) => {
          // Sempre substitui apenas o partial pendente do mesmo grupo.
          // Finais já confirmados não são apagados — Deepgram emite N
          // finais por utterance e todos viram linhas separadas. Sem
          // partial pendente: empurra como linha nova.
          const idx = prev.findIndex((s) => s.segmentGroupId === groupId && s.isPartial);
          const next = [...prev];
          if (idx >= 0) next[idx] = seg;
          else next.push(seg);
          return next.length > 500 ? next.slice(next.length - 500) : next;
        });
      } else if (t === 'metric.update') {
        setMetrics({
          talkRatioCloser: Number(payload.talk_ratio_closer),
          talkRatioCustomer: Number(payload.talk_ratio_customer),
          wpmCloser: Number(payload.wpm_closer),
          silenceSecondsTotal: Number(payload.silence_seconds_total),
        });
      } else if (t === 'insight.emitted') {
        const insight = payload.insight as Insight;
        if (insight !== undefined && insight !== null) {
          setInsights((prev) => {
            if (prev.some((i) => i.id === insight.id)) return prev;
            return [insight, ...prev].slice(0, 100);
          });
        }
      } else if (t === 'insight.updated') {
        const insightId = String(payload.insight_id);
        const patch = (payload.patch ?? {}) as Partial<Insight>;
        setInsights((prev) =>
          prev.map((i) => (i.id === insightId ? ({ ...i, ...patch } as Insight) : i)),
        );
      }
    });

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [sessionId, accessToken, role, wsBase]);

  if (status === 'forbidden') {
    return (
      <section style={{ padding: '1.5rem' }}>
        <h1>Reunião ao vivo</h1>
        <p>Acesso restrito a manager e admin do tenant.</p>
      </section>
    );
  }

  return (
    <section style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1>Reunião ao vivo (observer)</h1>
        <p style={{ opacity: 0.7 }}>
          Sessão {sessionId} — {status === 'connected' ? 'conectado' : status}
          {closeReason !== null ? ` — ${closeReason}` : ''}
        </p>
      </header>

      {metrics !== null ? (
        <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem' }}>
          <span>Closer: {Math.round(metrics.talkRatioCloser * 100)}%</span>
          <span>Cliente: {Math.round(metrics.talkRatioCustomer * 100)}%</span>
          <span>WPM closer: {metrics.wpmCloser.toFixed(0)}</span>
          <span>Silêncio: {metrics.silenceSecondsTotal.toFixed(1)}s</span>
        </div>
      ) : null}

      <InsightsLiveList insights={insights} />

      <ol
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          maxHeight: '70vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        {segments.length === 0 ? (
          <li style={{ opacity: 0.6 }}>Aguardando primeiros segmentos…</li>
        ) : (
          segments.map((s) => (
            <li
              key={s.key}
              style={{
                fontStyle: s.isPartial ? 'italic' : 'normal',
                opacity: s.isPartial ? 0.7 : 1,
                display: 'flex',
                gap: '0.75rem',
              }}
            >
              <span style={{ fontWeight: 600, minWidth: 80 }}>{speakerLabel(s)}</span>
              <span>{s.text}</span>
            </li>
          ))
        )}
      </ol>
    </section>
  );
}
